/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Stripe Product and Price IDs
 *
 * All IDs are read from Vite environment variables at build time, with the
 * known production values as fallbacks. Override via VITE_* vars in .env files
 * to point at Stripe test-mode products without changing source code.
 *
 * Naming convention:
 *   Env var:  VITE_STRIPE_PRODUCT_ID_<IDENTIFIER>
 *   Constant: <PRODUCT>_PRODUCT_ID
 *
 * Keep in sync with backend/apps/core/constants.py.
 */

// ─── Helper ──────────────────────────────────────────────────────────────────
// Reads a Vite env var; returns the fallback when the var is absent or empty.
function envOr(key: string, fallback: string): string {
  const val = (import.meta.env as Record<string, string | undefined>)[key];
  return val && val.length > 0 ? val : fallback;
}

// =============================================================================
// Stripe Product IDs — Legacy product catalog
// =============================================================================

export const DOCUMENT_TRANSLATOR_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DOCUMENT_TRANSLATOR",
  "prod_S4ZfGm8LlY2gfS",
);

export const TSL_TRIAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_TRIAL",
  "prod_SWP8gzw03wNeHF",
);

export const TSL_45K_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_45K",
  "prod_SWL3Eco3BVssKP",
);

export const TSL_100K_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_100K",
  "prod_SWL7DsqVSJcF3B",
);

export const TSL_EXTRA_WORDS_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_TSL_EXTRA_WORDS",
  "prod_S4cvw2mR4nk8rc",
);

export const LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL",
  "prod_SWJs7eprCIowqR",
);

export const LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME",
  "prod_SWJy1kqYxfB8E2",
);

export const LEGACY_MSR_ANNUAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_LEGACY_ANNUAL",
  "prod_SWKk9lK3bixFBf",
);

export const LEGACY_MSR_LIFETIME_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_LEGACY_LIFETIME",
  "prod_SWKrEuGRlYYZiT",
);

export const LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL",
  "prod_SWOM5rPtGRexS8",
);

export const LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME",
  "prod_SWOOo2bNffsihz",
);

export const LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL",
  "prod_SWOEKGx9dXGNVn",
);

export const LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME",
  "prod_SWOI0ZgYTbu6nS",
);

// =============================================================================
// Stripe Product IDs — SK-U Unified product catalog
// =============================================================================

export const MSR_V6_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_V6",
  "prod_UFrLUPe8ekrE0z",
);

export const DICTATION_PRO_V2_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_DICTATION_PRO_V2",
  "prod_UIlZjf2HsfbSk4",
);

export const MSR_PRO_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_MSR_PRO",
  "prod_UIlpn9zTwvLgBg",
);

export const AUTO_FTP_V8_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_AUTO_FTP_V8",
  "prod_UIlwSb1mEsMvZd",
);

export const SECURITY_MONITOR_PRO_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_SECURITY_MONITOR_PRO",
  "prod_UIm2aTIZIn87Yh",
);

export const WEBCAM_MONITOR_PRODUCT_ID = envOr(
  "VITE_STRIPE_PRODUCT_ID_WEBCAM_MONITOR",
  "prod_UIm90SjjbJ1Gmc",
);

// =============================================================================
// QLM Product IDs (F-1, S343)
// Numeric QLM product IDs that map QLM license records back to Stripe products.
// Use actual QLM IDs as fallbacks (consistent with Stripe ID pattern above).
// =============================================================================

export const QLM_PRODUCT_ID_TSL_TRIAL = envOr("VITE_QLM_PRODUCT_ID_TSL_TRIAL", "631");
// Issue 9 (FCR Pass 9): both TSL paid plans share QLM ProductID 22. Tier is
// identified by Features ("0:2;" = 45K, "0:4;" = 100K), never by this ID. Single
// source of truth so the frontend cannot drift into a duplicate-ID hazard.
export const QLM_PRODUCT_ID_TSL = envOr("VITE_QLM_PRODUCT_ID_TSL", "22");
export const QLM_PRODUCT_ID_TSL_45K = QLM_PRODUCT_ID_TSL;
export const QLM_PRODUCT_ID_TSL_100K = QLM_PRODUCT_ID_TSL;
export const QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL = envOr("VITE_QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL", "712");
export const QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME = envOr("VITE_QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME", "701");
export const QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL = envOr("VITE_QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL", "1801");
export const QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME = envOr("VITE_QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME", "1811");
export const QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL = envOr("VITE_QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL", "1201");
export const QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME = envOr("VITE_QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME", "1202");
export const QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL = envOr("VITE_QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL", "1701");
export const QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME = envOr("VITE_QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME", "1702");
// DD-3 (FCR Pass 6): Unified product QLM IDs — confirmed from backend constants.py.
// These were 0 (placeholder) which disabled ProductID matching for all six products.
export const QLM_PRODUCT_ID_MSR_V6               = envOr("VITE_QLM_PRODUCT_ID_MSR_V6", "19");
export const QLM_PRODUCT_ID_DICTATION_PRO_V2     = envOr("VITE_QLM_PRODUCT_ID_DICTATION_PRO_V2", "21");
export const QLM_PRODUCT_ID_MSR_PRO              = envOr("VITE_QLM_PRODUCT_ID_MSR_PRO", "17");
export const QLM_PRODUCT_ID_AUTO_FTP_V8          = envOr("VITE_QLM_PRODUCT_ID_AUTO_FTP_V8", "7");
export const QLM_PRODUCT_ID_SECURITY_MONITOR_PRO = envOr("VITE_QLM_PRODUCT_ID_SECURITY_MONITOR_PRO", "26");
export const QLM_PRODUCT_ID_WEBCAM_MONITOR       = envOr("VITE_QLM_PRODUCT_ID_WEBCAM_MONITOR", "24");
